import React, { useState, useRef, useEffect } from 'react';
import { Dialog, Typography, Slider, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download.svg';
import { ReactComponent as FileDocumentIcon } from '../../assets/icons/file-document.svg';
import { downloadFile } from '../../utils/attachments';
import { getViewUrl } from '../../redux/attachments/service';
import useAppDispatch from '../../hooks/useAppDispatch';
import color from '../../theme/color';
import * as S from './styled';

interface AttachmentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    url: string;
    type: string;
    name: string;
    fileId: number;
    currentIndex?: number;
    totalItems?: number;
    onNext?: () => void;
    /** Pass for waggle/chat attachments — popup delegates the full download to the caller. */
    onDownload?: () => Promise<void>;
}

const AttachmentPopup: React.FC<AttachmentPopupProps> = ({
    isOpen,
    onClose,
    url,
    type,
    name,
    fileId,
    currentIndex = 1,
    totalItems = 1,
    onNext,
    onDownload
}) => {
    const dispatch = useAppDispatch();
    const isImageType = /^image\/(jpeg|jpg|png|gif|bmp|svg)$/i.test(type);
    const isVideoType = /^video\//i.test(type);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState('00:00');
    const [duration, setDuration] = useState('00:00');

    const formatTime = (timeInSeconds: number): string => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`;
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const currentTimeValue = videoRef.current.currentTime;
            const durationValue = videoRef.current.duration || 0;
            setProgress((currentTimeValue / durationValue) * 100);
            setCurrentTime(formatTime(currentTimeValue));
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(formatTime(videoRef.current.duration));
        }
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleProgressChange = (
        _event: Event,
        newValue: number | number[]
    ) => {
        if (videoRef.current && typeof newValue === 'number') {
            const newTime = (videoRef.current.duration * newValue) / 100;
            videoRef.current.currentTime = newTime;
            setProgress(newValue);
            setCurrentTime(formatTime(newTime));
        }
    };

    const handleRewind = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = Math.max(
                0,
                videoRef.current.currentTime - 2
            );
        }
    };

    const handleForward = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = Math.min(
                videoRef.current.duration,
                videoRef.current.currentTime + 2
            );
        }
    };

    const handleNextAttachment = () => {
        if (onNext) {
            onNext();
        }
    };

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            if (onDownload) {
                await onDownload();
            } else {
                const response = await dispatch(
                    getViewUrl({ fileId })
                ).unwrap();
                await downloadFile(response.url, name || 'download');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime('00:00');
        setDuration('00:00');
    }, [url]);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth={false}
            PaperComponent={S.StyledDialogPaper}
        >
            <S.HeaderBox>
                <Typography variant="subtitle1" sx={S.TitleTypographyStyles}>
                    Attachment
                </Typography>
                <Typography variant="h6" sx={S.CounterTypographyStyles}>
                    {totalItems > 0 ? `${currentIndex}/${totalItems}` : '1/1'}
                </Typography>

                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={S.CloseIconButtonStyles}
                >
                    <CloseIcon />
                </IconButton>
            </S.HeaderBox>
            <S.FileInfoBox>
                <Typography variant="subtitle1" sx={S.FileNameTypographyStyles}>
                    {name}
                </Typography>

                <IconButton
                    onClick={handleDownload}
                    sx={S.DownloadIconButtonStyles}
                >
                    <DownloadIcon
                        width={16}
                        height={16}
                        style={{ color: color.white }}
                    />
                </IconButton>
            </S.FileInfoBox>

            <S.ContentBox>
                {isImageType ? (
                    <S.ImageContainer>
                        <S.StyledImage src={url} alt={name} />
                    </S.ImageContainer>
                ) : isVideoType ? (
                    <S.VideoContainer>
                        <S.VideoWrapper>
                            <S.StyledVideo
                                ref={videoRef}
                                src={url}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onClick={togglePlay}
                            />
                            {!isPlaying && (
                                <S.PlayButtonOverlay onClick={togglePlay}>
                                    <PlayArrowIcon sx={S.PlayIconStyles} />
                                </S.PlayButtonOverlay>
                            )}
                        </S.VideoWrapper>

                        <S.VideoControlsBox>
                            <Slider
                                value={progress}
                                onChange={handleProgressChange}
                                sx={S.SliderStyles}
                            />

                            <S.ControlsContainer>
                                <S.PlaybackControls>
                                    <IconButton
                                        size="small"
                                        onClick={handleRewind}
                                        sx={S.ControlIconButtonStyles}
                                    >
                                        <RotateLeftIcon />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        onClick={togglePlay}
                                        sx={S.ControlIconButtonWithMarginStyles}
                                    >
                                        {isPlaying ? (
                                            <PauseIcon />
                                        ) : (
                                            <PlayArrowIcon />
                                        )}
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        onClick={handleForward}
                                        sx={S.ControlIconButtonStyles}
                                    >
                                        <RotateRightIcon />
                                    </IconButton>
                                </S.PlaybackControls>

                                <Typography
                                    variant="body2"
                                    sx={S.TimeTypographyStyles}
                                >
                                    {`${currentTime} / ${duration}`}
                                </Typography>
                            </S.ControlsContainer>
                        </S.VideoControlsBox>
                    </S.VideoContainer>
                ) : (
                    <S.NoPreviewContainer>
                        <FileDocumentIcon width={80} height={80} />
                        <Typography variant="h6" sx={S.NoPreviewTextStyles}>
                            Preview not available for this file type
                        </Typography>
                        <Typography variant="body2" sx={S.NoPreviewTextStyles}>
                            Click the download icon to view this file
                        </Typography>
                    </S.NoPreviewContainer>
                )}
            </S.ContentBox>

            {totalItems > 1 && currentIndex < totalItems && (
                <S.NextButtonContainer>
                    <S.NextButton onClick={handleNextAttachment}>
                        <Typography
                            variant="button"
                            sx={S.NextButtonTextStyles}
                        >
                            Next
                        </Typography>
                    </S.NextButton>
                </S.NextButtonContainer>
            )}

            {currentIndex === totalItems && (
                <S.NextButtonContainer>
                    <S.NextButton onClick={onClose}>
                        <Typography
                            variant="button"
                            sx={S.NextButtonTextStyles}
                        >
                            Close
                        </Typography>
                    </S.NextButton>
                </S.NextButtonContainer>
            )}
        </Dialog>
    );
};

export default AttachmentPopup;
